import { selectUserProjectTimeZone } from 'core/userContext';
import _isNumber from 'lodash/isNumber';
import _upperFirst from 'lodash/upperFirst';

import { RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS } from 'modules/campaigns/editor/modals/ScheduleCampaignModal/ScheduleCampaignModal.constants';
import { getScheduleCampaignTargetTimeZone } from 'modules/campaigns/editor/modals/ScheduleCampaignModal/ScheduleCampaignModal.utils';
import {
  getIsNotLaunchedCampaign,
  getSendTimeExperimentVariants,
} from 'modules/campaigns/editor/utils';
import {
  getCampaignEndRecurrenceType,
  getCampaignRecurrenceScheduleType,
  getIsRecurringScheduleType,
  getSkipOnDaysText,
  pluralizeWord,
  skipDaysMapperToList,
} from 'modules/campaigns/lib/utils';
import { BaseSchedulerMsgParams, CampaignState } from 'modules/domains/campaign';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { P, match } from 'ts-pattern';
import { Nullable } from 'utils/tsHelpers';
import { stripTimezonePrefix } from 'utils/date';
import { useCampaignContext } from '../../../context';
import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import { LightText, SectionHeader, SectionWrapper } from '../Sections.styled';
import {
  getEndedTimeDisplayText,
  getIsRecipientTimeZoneScheduleType,
  getLaunchTimeDisplayText,
  getNextSendDateText,
  getScheduleEndRecurrenceTypeText,
  getScheduleRecurrenceFrequencyText,
  getTimeZoneName,
  getZoneAbbr,
} from './SchedulingSection.utils';
import { getIsEligibilityCampaign } from '../../utils';

const TimeZoneReadOnlyField = styled(ReadOnlyField)`
  display: inline-flex;
  justify-content: space-between;
  width: 100%;
`;

export const SCHEDULING_SECTION_TEST_ID = 'scheduling-section';

const CAMPAIGN_STATES_WITH_ONLY_SCHEDULING_TIME_AS_SCHEDULING_DETAIL = [
  CampaignState.Draft,
  CampaignState.Ready,
];

const TimeZoneDisplay = ({ timeZone }: { timeZone?: Nullable<string> }) => {
  return (
    match({ timeZone })
      // both name and abbr are the same for UTC
      .with({ timeZone: 'UTC' }, ({ timeZone: tz }) => <span>{tz}</span>)
      .with({ timeZone: P.string }, ({ timeZone: tz }) => (
        <>
          <span>{getTimeZoneName(tz)}</span>
          <LightText>{getZoneAbbr(tz)}</LightText>
        </>
      ))
      .otherwise(() => <LightText>Not set</LightText>)
  );
};

export const SchedulingSection = () => {
  const { campaign } = useCampaignContext();
  const isEligibilityCampaign = getIsEligibilityCampaign(campaign?.campaignTypeDefinition);

  const projectTimeZone = useSelector(selectUserProjectTimeZone);

  const { campaignState, endedAt, schedulerMsgParams } = campaign;
  const { precreateHours, startTimeZone, defaultTimeZone } =
    schedulerMsgParams || ({} as BaseSchedulerMsgParams);

  const targetTimeZone = getScheduleCampaignTargetTimeZone(
    projectTimeZone,
    startTimeZone,
    campaign.schedulerMsgParams?.scheduleType,
  );

  const nextRunTime = campaign?.nextRunTime;
  const recurrenceType = getCampaignRecurrenceScheduleType(campaign);
  const recurrenceText = getScheduleRecurrenceFrequencyText(recurrenceType, campaign);
  const endType = getCampaignEndRecurrenceType(campaign);
  const endRecurrenceTypeText = getScheduleEndRecurrenceTypeText(
    endType,
    campaign,
    projectTimeZone,
  );
  const skipOnDays = RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS.includes(recurrenceType)
    ? skipDaysMapperToList(campaign.schedulerMsgParams?.skipDaysOfWeek)
    : [];

  const isNotLaunchedCampaign = getIsNotLaunchedCampaign(campaign);
  const isRecurringScheduleType = getIsRecurringScheduleType(recurrenceType);
  const isRecipientTimeZoneScheduleType = getIsRecipientTimeZoneScheduleType(campaign);
  const shouldShowEndedTime = !isNotLaunchedCampaign && !isRecurringScheduleType && endedAt;

  /**
   * This flag is use to hide/show all scheduling details but launch time.
   * Draft and Ready campaigns don't displays all details available as it is confusing to customers.
   */
  const shouldShowAllSchedulingDetails =
    !CAMPAIGN_STATES_WITH_ONLY_SCHEDULING_TIME_AS_SCHEDULING_DETAIL.includes(campaignState);

  const sendTimeExperimentVariants = getSendTimeExperimentVariants(campaign?.experiments);

  const shouldShowRecipientTimeZoneInfo =
    shouldShowAllSchedulingDetails &&
    isRecipientTimeZoneScheduleType &&
    /**
     * there is an edge case where cloned campaign that had
     * startTimeZone and defaultTimeZone still have those fields after scheduling.
     * This hides that data as they're not supported (UI-editable) for experiments
     */
    !sendTimeExperimentVariants?.length;
  const shouldShowRecurrenceInfo = shouldShowAllSchedulingDetails && isRecurringScheduleType;
  const shouldShowEndedTimeInfo =
    shouldShowAllSchedulingDetails && shouldShowEndedTime && !isEligibilityCampaign;
  const shouldShowEligibilityEndingTimeInfo = isEligibilityCampaign && !isNotLaunchedCampaign;

  const launchTime = getLaunchTimeDisplayText(
    campaign?.startAt as string,
    targetTimeZone,
    isRecipientTimeZoneScheduleType,
    campaign?.experiments,
  );

  return (
    <SectionWrapper data-test={SCHEDULING_SECTION_TEST_ID}>
      <SectionHeader>Scheduling</SectionHeader>

      <CampaignFormField labelText="Launch time" $removeMarginBottom={!isRecurringScheduleType}>
        <ReadOnlyField name="scheduleStartTime">
          {isNotLaunchedCampaign ? <LightText>Not launched</LightText> : launchTime}
        </ReadOnlyField>
      </CampaignFormField>

      {shouldShowRecipientTimeZoneInfo && (
        <>
          <CampaignFormField labelText="Starting time zone">
            <TimeZoneReadOnlyField name="startingTimeZone">
              <TimeZoneDisplay timeZone={startTimeZone} />
            </TimeZoneReadOnlyField>
          </CampaignFormField>

          <CampaignFormField labelText="Fallback time zone">
            <TimeZoneReadOnlyField name="fallbackTimeZone">
              <TimeZoneDisplay timeZone={defaultTimeZone} />
            </TimeZoneReadOnlyField>
          </CampaignFormField>
        </>
      )}

      {shouldShowRecurrenceInfo && (
        <>
          <CampaignFormField labelText="Repeat">
            <ReadOnlyField name="recurrence">
              {_upperFirst(recurrenceText)}
              {skipOnDays.length ? `, skipping ${getSkipOnDaysText(skipOnDays, true)}` : ''}
            </ReadOnlyField>
          </CampaignFormField>

          {campaignState === CampaignState.Recurring && nextRunTime && (
            <CampaignFormField labelText="Next send">
              <ReadOnlyField name="nextRunTime">
                {getNextSendDateText(nextRunTime as string, projectTimeZone)}
              </ReadOnlyField>
            </CampaignFormField>
          )}

          {_isNumber(precreateHours) && (
            <CampaignFormField labelText="Precreate campaign">
              <ReadOnlyField name="precreate">
                {`${precreateHours} ${pluralizeWord('hour', precreateHours)} before next run time`}
              </ReadOnlyField>
            </CampaignFormField>
          )}

          <CampaignFormField labelText="Until">
            <ReadOnlyField name="until">{_upperFirst(endRecurrenceTypeText)}</ReadOnlyField>
          </CampaignFormField>
        </>
      )}

      {shouldShowEndedTimeInfo && (
        <CampaignFormField labelText="Ended time">
          <ReadOnlyField name="endedTime">
            {getEndedTimeDisplayText(endedAt as string, projectTimeZone)}
          </ReadOnlyField>
        </CampaignFormField>
      )}
      {shouldShowEligibilityEndingTimeInfo && (
        <CampaignFormField labelText="End for users">
          <ReadOnlyField name="endForUsers">
            {schedulerMsgParams?.endDate?.timestamp
              ? getEndedTimeDisplayText(
                  // Regex is needed here since fn expects iso format but comes with extraneous timezone details, 2023-10-31T07:00Z[Etc/UTC]
                  stripTimezonePrefix(schedulerMsgParams.endDate.timestamp),
                  projectTimeZone,
                )
              : 'Never'}
          </ReadOnlyField>
        </CampaignFormField>
      )}
    </SectionWrapper>
  );
};
